import { useAuthStore } from '#imports';
import { useTenantStore } from '#imports';
import { useClientPortalStore } from '#imports';
import { getStoredTenantId, isOfflineSession } from '~/lib/workspaceSession';
import { buildTenantWorkspaceUrl, resolveWorkspaceOrigin } from '~/lib/tenantLinks';

/**
 * Tenant Middleware
 *
 * Resolves the active tenant and verifies that the authenticated user has
 * access to it.  Two resolution strategies are supported:
 *
 * 1. Slug-based  – The tenant ID is read from the `[tenantSlug]` route param
 *    (default platform routing, e.g. /my-tenant/dashboard).
 *
 * 2. Domain-based – When no tenantSlug param is present the middleware
 *    attempts to resolve the tenant from the current hostname. This
 *    supports custom branded domains (e.g. app.mybrand.com/projects).
 *    On success the tenant context is set and the clean path-only URL is kept.
 *
 * Redirect rules:
 *  - Unauthenticated        → /auth/login
 *  - No tenant access       → /
 *  - Unknown custom domain  → /auth/login
 */
export default defineNuxtRouteMiddleware(async (to) => {
    if (import.meta.server) {
        return;
    }

    const runtimeConfig = useRuntimeConfig();
    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const portalStore = useClientPortalStore();
    const userStore = useUserStore();
    const { getHostname, isCustomDomain } = useDomainResolver();
    const storedTenantId = getStoredTenantId();
    const accessUnavailableError = () => createError({
        statusCode: 503,
        statusMessage: 'Workspace access could not be loaded. Please refresh and try again.',
    });
    const offlineSession = isOfflineSession();
    const activateOfflineTenant = async (tenantId: string) => {
        tenantStore.currentTenantId = tenantId;

        if (tenantStore.currentTenant?.id !== tenantId) {
            try {
                await tenantStore.fetchTenant(tenantId);
            } catch {
                // Offline fallback only needs the tenant id to keep the shell alive.
            }
        }
    };
    const canUseOfflineTenant = Boolean(offlineSession && storedTenantId);
    const matchesOfflineTenant = Boolean(canUseOfflineTenant && (!to.params.tenantSlug || to.params.tenantSlug === storedTenantId));
    const resolveTeamWorkspaceDestination = async () => {
        if (!authStore.user) return null;

        if (tenantStore.loadedForUserId !== authStore.user.uid) {
            const membershipsResolved = await tenantStore.fetchUserTenants(authStore.user.uid);
            if (!membershipsResolved) {
                throw accessUnavailableError();
            }
        }

        const fallbackTenantId = tenantStore.currentTenantId || tenantStore.tenants[0]?.tenantId || storedTenantId || '';
        return fallbackTenantId ? `/${fallbackTenantId}/my-hub` : null;
    };
    const navigateToRoleSafeDestination = async () => {
        if (userStore.hasTeamAccess) {
            const workspaceDestination = await resolveTeamWorkspaceDestination();
            if (workspaceDestination) {
                return navigateTo(workspaceDestination);
            }
        }

        if (canUseOfflineTenant && storedTenantId) {
            return navigateTo(`/${storedTenantId}/my-hub`);
        }

        const portalClientSlug = await portalStore.findFirstClientSlug();
        if (portalClientSlug) {
            return navigateTo(`/portal/${portalClientSlug}`);
        }

        if (userStore.hasTeamAccess) {
            const redirect = encodeURIComponent(to.fullPath || '/');
            return navigateTo(`/auth/login?redirect=${redirect}`);
        }

        return navigateTo('/onboarding');
    };

    const hostname = getHostname();
    const brandedWorkspaceHost = isCustomDomain(hostname);
    if (brandedWorkspaceHost) {
        const routeTenantSlug = typeof to.params.tenantSlug === 'string' ? to.params.tenantSlug.trim() : '';
        const resolvedTenantId = routeTenantSlug || await tenantStore.resolveTenantByDomain(hostname) || storedTenantId || '';
        if (resolvedTenantId) {
            const workspaceOrigin = resolveWorkspaceOrigin({
                appDomain: String(runtimeConfig.public.appDomain || ''),
                fallbackOrigin: window.location.origin,
            });
            const targetUrl = buildTenantWorkspaceUrl({
                origin: workspaceOrigin,
                tenantSlug: resolvedTenantId,
                path: to.fullPath || to.path || '/my-hub',
            });
            if (targetUrl !== window.location.href) {
                return navigateTo(targetUrl, { external: true, redirectCode: 302 });
            }
        }
    }

    await authStore.awaitAuthReady();

    if (!authStore.user) {
        const redirect = encodeURIComponent(to.fullPath || '/');
        return navigateTo(`/auth/login?redirect=${redirect}`);
    }

    if ((!userStore.user || userStore.user.id !== authStore.user.uid) && !matchesOfflineTenant) {
        const profileResolved = await userStore.fetchUser(authStore.user.uid);
        if (!profileResolved) {
            throw accessUnavailableError();
        }
    }

    if (!userStore.hasTeamAccess) {
        if (canUseOfflineTenant && storedTenantId) {
            if (!to.params.tenantSlug) {
                return navigateTo(`/${storedTenantId}${to.path === '/' ? '/my-hub' : to.path}`);
            }

            if (to.params.tenantSlug === storedTenantId) {
                await activateOfflineTenant(storedTenantId);
                return;
            }

            return navigateTo(`/${storedTenantId}/my-hub`);
        }

        const portalClientSlug = await portalStore.findFirstClientSlug();
        if (portalClientSlug) {
            return navigateTo(`/portal/${portalClientSlug}`);
        }
        return navigateTo('/onboarding');
    }

    const isSecurityRoute = to.path.endsWith('/account/security');
    const resolveSecurityPath = (slug: string | null) => {
        if (slug) {
            return `/${slug}/account/security`;
        }

        return '/account/security';
    };

    let tenantSlug = to.params.tenantSlug as string;

    if (offlineSession && tenantSlug && storedTenantId === tenantSlug) {
        await activateOfflineTenant(tenantSlug);
        return;
    }

    // ── Domain-based fallback ─────────────────────────────────────────────
    // When there is no slug in the route (e.g. accessing a custom domain
    // directly), try to resolve the tenant from the current hostname.
    if (!tenantSlug) {
        if (isCustomDomain(hostname)) {
            const tenantId = await tenantStore.resolveTenantByDomain(hostname);
            if (tenantId) {
                await tenantStore.setCurrentTenant(tenantId);

                await authStore.loadMfaStatus();
                if (authStore.tenantMfaEnforced && !authStore.hasTotpEnrolled && !isSecurityRoute) {
                    const redirect = encodeURIComponent(to.fullPath || to.path || '/');
                    return navigateTo(`${resolveSecurityPath(null)}?redirect=${redirect}`);
                }

                return;
            }

            // Authenticated platform users should still be able to continue via
            // their tenant membership even if the hostname was misclassified or
            // a custom-domain lookup is temporarily unavailable.
            if (tenantStore.loadedForUserId !== authStore.user.uid) {
                const membershipsResolved = await tenantStore.fetchUserTenants(authStore.user.uid);
                if (!membershipsResolved) {
                    throw accessUnavailableError();
                }
            }
            if (tenantStore.currentTenantId) {
                await tenantStore.setCurrentTenant(tenantStore.currentTenantId);

                await authStore.loadMfaStatus();
                if (authStore.tenantMfaEnforced && !authStore.hasTotpEnrolled && !isSecurityRoute) {
                    const redirect = encodeURIComponent(to.fullPath || to.path || '/');
                    return navigateTo(`${resolveSecurityPath(null)}?redirect=${redirect}`);
                }

                return;
            }
        }

        const fallbackTenantId = tenantStore.currentTenantId || tenantStore.tenants[0]?.tenantId || '';
        if (fallbackTenantId) {
            return navigateTo(`/${fallbackTenantId}${to.path === '/' ? '/my-hub' : to.path}`);
        }

        if (offlineSession && storedTenantId) {
            return navigateTo(`/${storedTenantId}/my-hub`);
        }

        const redirect = encodeURIComponent(to.fullPath || '/');
        return navigateTo(`/auth/login?redirect=${redirect}`);
    }

    // ── Slug-based resolution (standard flow) ─────────────────────────────

    if (canUseOfflineTenant && storedTenantId) {
        if (tenantSlug === storedTenantId) {
            await activateOfflineTenant(storedTenantId);
            return;
        }

        return navigateTo(`/${storedTenantId}/my-hub`);
    }

    // Lazy-load user tenant list on first access
    if (tenantStore.loadedForUserId !== authStore.user.uid) {
        const membershipsResolved = await tenantStore.fetchUserTenants(authStore.user.uid);
        if (!membershipsResolved) {
            throw accessUnavailableError();
        }
    }

    const hasAccess = tenantStore.tenants.some(t => t.tenantId === tenantSlug);

    if (!hasAccess) {
        return navigateToRoleSafeDestination();
    }

    // Switch active tenant when navigating between tenants
    if (tenantStore.currentTenantId !== tenantSlug) {
        await tenantStore.setCurrentTenant(tenantSlug);
    }

    await authStore.loadMfaStatus();

    if (authStore.tenantMfaEnforced && !authStore.hasTotpEnrolled && !isSecurityRoute) {
        const redirect = encodeURIComponent(to.fullPath || to.path || '/');
        return navigateTo(`${resolveSecurityPath(tenantSlug)}?redirect=${redirect}`);
    }
});
